import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { RequireAccess } from '../components/RequireAccess';
import { LotCard } from '../components/LotCard';
import { Skeleton } from '../components/Skeleton';
import { EmptyState } from '../components/EmptyState';
import { DotRule } from '../components/DotRule';
import { EyeIcon } from '../components/WatchButton';
import { fetchSalesRoomPosts, type SalesRoomBuckets } from '../lib/queries';
import type { Post } from '../lib/types';
import './SalesRoom.css';

/**
 * /sales-room — members-only lens over posts with the auction feature.
 *
 * Three buckets pulled in one round-trip (see fetchSalesRoomPosts):
 *   1. Live now       — sorted by ends_at ascending
 *   2. Coming soon    — sorted by starts_at ascending
 *   3. Recently ended — last 30 days, ends_at descending
 *
 * Each section renders only if its bucket is non-empty. When all three
 * are empty the page falls back to a single EmptyState.
 */
export default function SalesRoom() {
  return (
    <RequireAccess>
      <SalesRoomInner />
    </RequireAccess>
  );
}

function SalesRoomInner() {
  const [buckets, setBuckets] = useState<SalesRoomBuckets | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const ctrl = new AbortController();
    (async () => {
      try {
        const data = await fetchSalesRoomPosts(ctrl.signal);
        if (ctrl.signal.aborted) return;
        setBuckets(data);
      } catch (err: any) {
        if (ctrl.signal.aborted || err?.name === 'AbortError') return;
        console.error('[sales-room] fetch failed', err);
        setError(err?.message ?? 'Something broke loading the Saleroom.');
      } finally {
        if (!ctrl.signal.aborted) setLoading(false);
      }
    })();
    return () => ctrl.abort();
  }, []);

  const isEmpty =
    !loading &&
    !error &&
    buckets !== null &&
    buckets.live.length === 0 &&
    buckets.scheduled.length === 0 &&
    buckets.ended.length === 0;

  return (
    <div className="sales-room">
      <header className="sales-room__header">
        <div className="container">
          <div className="sales-room__header-top">
            <p className="kicker">Saleroom</p>
            <Link to="/watchlist" className="sales-room__to-watchlist">
              <EyeIcon />
              Watchlist
            </Link>
          </div>
          <h1 className="sales-room__headline">Lots, live and on deck.</h1>
          <p className="sales-room__intro">
            One-of-one pieces, listed individually as Kate finishes them. Bidding
            is in US dollars; auctions auto-extend two minutes when a bid lands
            in the final stretch.
          </p>
          <DotRule className="sales-room__rule" />
        </div>
      </header>

      <div className="container">
        {loading ? (
          <SkeletonGrid />
        ) : error ? (
          <EmptyState
            eyebrow="Error"
            headline="We couldn't load the Saleroom."
            body={error}
          />
        ) : isEmpty ? (
          <EmptyState
            eyebrow="Quiet for now"
            headline="No lots in the Saleroom yet."
            body="Watch this space; pieces drop as Kate finishes them."
          />
        ) : (
          buckets && (
            <>
              <LotSection title="Live now" posts={buckets.live} />
              <LotSection title="Coming soon" posts={buckets.scheduled} />
              {/* "Recently closed", not "Sold" — the bucket holds unsold lots too. */}
              <LotSection title="Recently closed" posts={buckets.ended} />
            </>
          )
        )}
      </div>
    </div>
  );
}

function LotSection({ title, posts }: { title: string; posts: Post[] }) {
  if (posts.length === 0) return null;
  return (
    <section className="sales-room__section">
      <div className="sales-room__section-head">
        <h2 className="sales-room__section-title">{title}</h2>
        <span className="sales-room__section-count">{posts.length}</span>
      </div>
      <div className="sales-room__grid">
        {posts.map((p) => (
          <LotCard key={p.id} post={p} />
        ))}
      </div>
    </section>
  );
}

function SkeletonGrid() {
  return (
    <section className="sales-room__section" aria-busy="true" aria-label="Loading lots">
      <div className="sales-room__grid">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="sales-room__skel-cell">
            <Skeleton variant="card" aspectRatio={4 / 5} />
            <div className="sales-room__skel-meta">
              <Skeleton variant="line" width="70%" height={18} />
              <Skeleton variant="line" width="45%" height={12} />
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
